import { Alert } from "@opowiedzo/ui";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { ArrowLeftIcon, ChevronLeftIcon, ChevronRightIcon } from "@/components/Icon";
import { plural } from "@/lib/format";

/**
 * Powrót do listy. Detal otwiera się z tabeli, więc droga w drugą stronę
 * musi być widoczna bez sięgania po przycisk przeglądarki.
 */
export function BackLink({ to, children }: { to: string; children: string }) {
  return (
    <Link
      to={to}
      className="mb-3 inline-flex items-center gap-1.5 text-base text-ink-soft hover:text-ink"
    >
      <ArrowLeftIcon />
      {children}
    </Link>
  );
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 className="font-display text-xl font-semibold text-ink">{title}</h1>
        {description && <p className="mt-1 max-w-3xl text-md text-ink-soft">{description}</p>}
      </div>
      {actions}
    </header>
  );
}

export function Card({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <section className="rounded-card border border-line bg-surface px-5 py-4.5">
      {title && <h2 className="mb-3.5 font-display text-lg font-semibold text-ink">{title}</h2>}
      {children}
    </section>
  );
}

/** Notka pod kartą: dlaczego liczba nad nią znaczy mniej, niż się wydaje. */
export function CardNote({ children }: { children: ReactNode }) {
  return <p className="mt-3.5 text-xs leading-relaxed text-ink-soft">{children}</p>;
}

export function Loading() {
  return <p className="py-10 text-center text-base text-ink-soft">Wczytuję...</p>;
}

/** Błąd pisany pełnym zdaniem (rozdz. 21 docs/product.md), nie kodem HTTP. */
export function ErrorBox({ error }: { error: { message?: string } | null | undefined }) {
  if (!error) return null;
  return <Alert tone="error">{error.message ?? "Nie udało się pobrać danych."}</Alert>;
}

export function Pagination({
  page,
  pageSize,
  total,
  onChange,
}: {
  page: number;
  pageSize: number;
  total: number;
  onChange: (page: number) => void;
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const first = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);

  return (
    <div className="mt-3.5 flex flex-wrap items-center justify-between gap-3 text-base text-ink-soft">
      <p className="tabular">
        {total === 0
          ? "Nic tu jeszcze nie ma."
          : `${first}-${last} z ${total} ${plural(total, "pozycji", "pozycje", "pozycji")}`}
      </p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-card border border-line bg-surface px-3 py-1.5 text-ink transition-colors hover:border-ink-soft disabled:cursor-not-allowed disabled:opacity-40"
          disabled={page <= 1}
          onClick={() => onChange(page - 1)}
        >
          <ChevronLeftIcon />
          Poprzednia
        </button>
        <span className="tabular">
          {page} / {pages}
        </span>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-card border border-line bg-surface px-3 py-1.5 text-ink transition-colors hover:border-ink-soft disabled:cursor-not-allowed disabled:opacity-40"
          disabled={page >= pages}
          onClick={() => onChange(page + 1)}
        >
          Następna
          <ChevronRightIcon />
        </button>
      </div>
    </div>
  );
}

export interface SelectOption {
  value: string;
  label: string;
}

export function FilterBar({ children }: { children: ReactNode }) {
  return <div className="mb-3.5 flex flex-wrap items-end gap-3">{children}</div>;
}

export function Select({
  label,
  value,
  options,
  emptyLabel = "Wszystkie",
  onChange,
}: {
  label: string;
  value: string;
  options: SelectOption[];
  emptyLabel?: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1 text-base text-ink-soft">
      {label}
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="min-w-44 rounded-card border border-line bg-surface px-2.5 py-2 text-base text-ink"
      >
        <option value="">{emptyLabel}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function SearchInput({
  label,
  value,
  placeholder,
  onChange,
}: {
  label: string;
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1 text-base text-ink-soft">
      {label}
      <input
        type="search"
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        className="w-65 rounded-card border border-line bg-surface px-2.5 py-2 text-base text-ink placeholder:text-ink-soft/60"
      />
    </label>
  );
}

export function DefinitionList({
  rows,
  /** Nazwy kolejek i kluczy technicznych czyta się jak kod, nie jak zdanie. */
  monoLabels = false,
}: {
  rows: [string, ReactNode][];
  monoLabels?: boolean;
}) {
  return (
    <dl className="grid grid-cols-[minmax(7rem,auto)_1fr] gap-x-4.5 gap-y-2 text-base">
      {rows.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className={`text-ink-soft ${monoLabels ? "font-mono text-sm" : ""}`}>{label}</dt>
          <dd className="text-ink">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Siatka detalu: treść po lewej, decyzje po prawej. */
export function DetailGrid({ main, side }: { main: ReactNode; side: ReactNode }) {
  return (
    <div className="grid items-start gap-4.5 xl:grid-cols-[2fr_1fr]">
      <div className="flex flex-col gap-4.5">{main}</div>
      <div className="flex flex-col gap-4.5">{side}</div>
    </div>
  );
}
